/**
 * FP3-105A — Super Admin permanent provider/assistant deletion modal.
 *
 * Danger Zone action. Preview (blockers + counts + Former Provider
 * disclosure) → strict confirmation (exact name + "DELETE PROVIDER" +
 * checkbox) → resumable staged job → progress → completed / failed
 * (+ Resume). Historical student records are preserved; only auth +
 * provider-private data are removed.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ACTIVE_LABELS = {
  current_students: "Current student relationships",
  supervised_assistants: "Assistants supervised",
  student_level_assistant_relationships: "Student-level assistant assignments",
  future_schedule_blocks: "Future schedule blocks",
  upcoming_sessions: "Upcoming sessions",
  pending_invites: "Pending invites",
};

const PRESERVED_LABELS = {
  historical_students: "Historical student relationships",
  historical_goals: "Goals (attribution kept)",
  historical_sessions: "Completed sessions (attribution kept)",
  historical_notes: "Notes (attribution kept)",
};

const STAGE_LABELS = {
  access_locked: "Lock access",
  auth_revoked: "Revoke tokens & invites",
  blockers_verified: "Verify no active relationships",
  private_data_deleted: "Delete provider-private data",
  historical_attribution_preserved: "Preserve Former Provider attribution",
  active_provider_deleted: "Delete account & roster record",
  orphan_verification: "Verify no access remains",
  complete: "Complete",
};

const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    if (Array.isArray(d.blockers)) return d.blockers.join(" ");
    return d.message || JSON.stringify(d);
  }
  return e?.message || "Request failed";
};

export default function PlatformProviderDeleteModal({
  open,
  onOpenChange,
  provider, // { id, name }
  onDeleted,
}) {
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [ack, setAck] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [job, setJob] = useState(null);
  const [resuming, setResuming] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setPreviewError("");
      setConfirmName("");
      setConfirmPhrase("");
      setAck(false);
      setApplyError("");
      setJob(null);
      return;
    }
    setLoading(true);
    api
      .platformProviderDeletePreview(provider.id)
      .then((r) => setPreview(r.data))
      .catch((e) => setPreviewError(errDetail(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!job?.id || job.status !== "running") return undefined;
    pollRef.current = setInterval(() => {
      api.platformDeletionJob(job.id).then((r) => setJob(r.data)).catch(() => {});
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  const blockers = preview?.blockers || [];
  const nameOk = confirmName.trim() === (provider?.name || "").trim();
  const phraseOk = confirmPhrase.trim() === "DELETE PROVIDER";
  const canApply =
    !!preview && blockers.length === 0 && nameOk && phraseOk && ack && !applying;

  const apply = async () => {
    setApplying(true);
    setApplyError("");
    try {
      const r = await api.platformProviderDeleteApply(provider.id, {
        preview_token: preview.preview_token,
        confirm_name: confirmName.trim(),
        confirm_phrase: confirmPhrase.trim(),
        expected_counts: preview.counts,
      });
      const d = r.data || {};
      if (d.idempotent && d.job) setJob(d.job);
      else
        setJob({
          id: d.job_id,
          status: "running",
          stages: d.stages || [],
          stages_completed: [],
          current_stage: (d.stages || [])[0],
        });
    } catch (e) {
      setApplyError(errDetail(e));
    } finally {
      setApplying(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.platformDeletionJobResume(job.id);
      setJob((j) => ({ ...j, status: "running", error: null }));
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setResuming(false);
    }
  };

  const c = preview?.counts || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[88vh] overflow-y-auto"
        data-testid="provider-delete-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[#7A1E1E]">
            Permanently delete provider
          </DialogTitle>
          <DialogDescription data-testid="provider-delete-warning">
            This permanently removes {provider?.name}'s account and active
            roster record and cannot be undone. Official student history is
            preserved and re-attributed to a read-only "Former Provider"
            reference that cannot sign in.
          </DialogDescription>
        </DialogHeader>

        {job ? (
          <JobProgress
            job={job}
            name={provider?.name || ""}
            resuming={resuming}
            onResume={resume}
            onDone={() => onDeleted?.(job)}
          />
        ) : (
          <>
            {loading && (
              <p className="text-[12px] text-[#6B7280]" data-testid="provider-delete-loading">
                Calculating impact…
              </p>
            )}
            {previewError && (
              <p className="text-[12px] text-[#B85C5C]" data-testid="provider-delete-preview-error">
                {previewError}
              </p>
            )}
            {preview && (
              <div className="space-y-3">
                {blockers.length > 0 && (
                  <div
                    className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] px-2.5 py-2 space-y-1"
                    data-testid="provider-delete-blockers"
                  >
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-[#7A1E1E]">
                      Resolve before deleting
                    </p>
                    {blockers.map((b, i) => (
                      <p key={i} className="text-[11px] font-medium text-[#B85C5C]">
                        ✕ {b}
                      </p>
                    ))}
                    <p className="text-[10.5px] text-[#8A6B5A] pt-1">
                      Tip: you can Disable the account now to block access,
                      then resolve these, then permanently delete.
                    </p>
                  </div>
                )}

                <div
                  className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] p-3"
                  data-testid="provider-delete-active-counts"
                >
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[#7A1E1E] mb-1.5">
                    Active access & private data (removed)
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {Object.entries(ACTIVE_LABELS).map(([k, label]) => (
                      <div
                        key={k}
                        className="flex items-center justify-between text-[11.5px]"
                        data-testid={`provider-delete-count-${k}`}
                      >
                        <span className="text-[#3F4756]">{label}</span>
                        <span className="font-semibold tabular-nums text-[#1F2937]">
                          {Number(c[k] ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="rounded-md border border-[#CFE0D6] bg-[#F2F8F4] p-3"
                  data-testid="provider-delete-preserved-counts"
                >
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-[#1E5A3F] mb-1.5">
                    Preserved as Former Provider attribution
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {Object.entries(PRESERVED_LABELS).map(([k, label]) => (
                      <div
                        key={k}
                        className="flex items-center justify-between text-[11.5px]"
                      >
                        <span className="text-[#3F4756]">{label}</span>
                        <span className="font-semibold tabular-nums text-[#1F2937]">
                          {Number(c[k] ?? 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {blockers.length === 0 && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">
                        Type the exact provider name:{" "}
                        <span className="font-mono font-semibold">{provider?.name}</span>
                      </Label>
                      <Input
                        data-testid="provider-delete-name-input"
                        value={confirmName}
                        onChange={(e) => setConfirmName(e.target.value)}
                        className="h-8 text-[12px]"
                        placeholder={provider?.name}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[11px]">
                        Type <span className="font-mono font-semibold">DELETE PROVIDER</span> to confirm
                      </Label>
                      <Input
                        data-testid="provider-delete-phrase-input"
                        value={confirmPhrase}
                        onChange={(e) => setConfirmPhrase(e.target.value)}
                        className="h-8 text-[12px] font-mono"
                        placeholder="DELETE PROVIDER"
                        autoComplete="off"
                      />
                    </div>
                    <label className="flex items-start gap-2 text-[11.5px] text-[#3F4756] cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="provider-delete-ack-checkbox"
                        checked={ack}
                        onChange={(e) => setAck(e.target.checked)}
                        className="mt-0.5 accent-[#7A1E1E]"
                      />
                      <span>
                        I understand this permanently removes this provider's
                        account and access and cannot be undone.
                      </span>
                    </label>
                  </>
                )}

                {applyError && (
                  <p className="text-[12px] text-[#B85C5C]" data-testid="provider-delete-apply-error">
                    {applyError}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {!job && (
          <DialogFooter>
            <Button
              data-testid="provider-delete-cancel-btn"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid="provider-delete-apply-btn"
              size="sm"
              disabled={!canApply}
              onClick={apply}
              className="bg-[#7A1E1E] hover:bg-[#611414] text-white disabled:opacity-40"
            >
              {applying ? "Starting…" : "Permanently delete provider"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function JobProgress({ job, name, resuming, onResume, onDone }) {
  const stages = job.stages || [];
  const done = new Set(job.stages_completed || []);
  const completed = job.status === "completed";
  const failed = job.status === "failed";
  return (
    <div className="space-y-3" data-testid="provider-delete-job-progress">
      <p className="text-[12px] text-[#3F4756]">
        {completed
          ? `${name} has been permanently deleted. Historical records now show "Former Provider".`
          : failed
            ? "The deletion stopped before completing. No success is reported — resume when ready (all steps are idempotent)."
            : `Deleting ${name}… This runs server-side and continues if you close this dialog.`}
      </p>
      <ul className="space-y-1 rounded-md border border-[#E5E3DB] bg-[#FBFAF6] px-3 py-2.5">
        {stages.map((s) => {
          const isDone = done.has(s) || completed;
          const isCurrent = !isDone && job.current_stage === s && job.status === "running";
          return (
            <li
              key={s}
              data-testid={`provider-delete-stage-${s}`}
              className={`flex items-center gap-2 text-[11.5px] ${
                isDone ? "text-[#1E5A3F]" : isCurrent ? "text-[#1F2937] font-medium" : "text-[#9AA0A6]"
              }`}
            >
              <span className="w-4 text-center">
                {isDone ? "✓" : isCurrent ? "…" : "·"}
              </span>
              {STAGE_LABELS[s] || s}
            </li>
          );
        })}
      </ul>
      {failed && (
        <div className="rounded-md border border-[#EAD5D0] bg-[#FBF3F1] px-2.5 py-2" data-testid="provider-delete-job-failed">
          <p className="text-[11px] font-medium text-[#B85C5C]">{job.error || "Deletion failed."}</p>
          {job.orphan_report && Object.keys(job.orphan_report).length > 0 && (
            <p className="text-[10.5px] text-[#6B7280] mt-1">
              Remaining: {Object.entries(job.orphan_report).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          )}
        </div>
      )}
      <DialogFooter>
        {failed && (
          <Button
            data-testid="provider-delete-resume-btn"
            size="sm"
            onClick={onResume}
            disabled={resuming}
            className="bg-[#7A1E1E] hover:bg-[#611414] text-white"
          >
            {resuming ? "Resuming…" : "Resume deletion"}
          </Button>
        )}
        {completed && (
          <Button
            data-testid="provider-delete-done-btn"
            size="sm"
            onClick={onDone}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            Done
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}
